import React, { useState, useEffect, useCallback, useMemo } from "react";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useNavigate, useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./HostDashboard.module.sass";
import Icon from "../../components/Icon";
import { useChatContext } from "../../contexts/ChatContext";
import Reviews from "../../components/Reviews";
import PendingReviews from "../../components/PendingReviews";
import InfoPage from "../InfoPage";
import Settings from "../../components/Settings";
import { getPropertyLabel, getPropertyLabelLower } from "../../utils/labelHelpers";
import { usePersona } from "../../contexts/PersonaContext";
import IntroVideoUpload from "../../components/IntroVideoUpload";
import ShowcaseUpload from "../../components/ShowcaseUpload";

const menuItems = [
    { title: "Overview", icon: "home" },
    { title: "Profile", icon: "user" },
    { title: "Properties", icon: "building" },
    { title: "Bookings", icon: "credit-card" },
    { title: "Messages", icon: "comment" },
    { title: "Reviews", icon: "star" },
    { title: "Payouts", icon: "coin" },
    { title: "Calendar", icon: "calendar" },
    { title: "Settings", icon: "settings" },
];

// Property Limit Progress Component
const PropertyLimitProgress = ({ currentCount, maxProperties, planName, onUpgrade, hostCategory }) => {
    const percentage = Math.min((currentCount / maxProperties) * 100, 100);
    const propertyLabel = getPropertyLabel(hostCategory);
    const propertiesLabel = getPropertyLabel(hostCategory, true);
    const propertiesLabelLower = getPropertyLabelLower(hostCategory, true);
    
    // Determine color based on usage percentage
    let progressColor = '#10B981'; // green
    let textColor = '#059669';
    if (percentage >= 80) {
        progressColor = '#EF4444'; // red
        textColor = '#DC2626';
    } else if (percentage >= 50) {
        progressColor = '#F59E0B'; // yellow
        textColor = '#D97706';
    }
    
    const isAtLimit = currentCount >= maxProperties;
    const remainingProperties = Math.max(0, maxProperties - currentCount);
    
    return (
        <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            border: '1px solid #E5E7EB'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
            }}>
                <div>
                    <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '4px'
                    }}>
                        {propertyLabel} Limit - {planName}
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: textColor,
                        fontWeight: '500'
                    }}>
                        {currentCount} of {maxProperties} {propertiesLabelLower} used
                    </div>
                </div>
                
                {percentage >= 80 && !isAtLimit && (
                    <button 
                        onClick={onUpgrade}
                        style={{
                            backgroundColor: 'transparent',
                            color: '#dc1d08',
                            border: '1px solid #dc1d08',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#dc1d08';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#dc1d08';
                        }}
                    >
                        Upgrade Plan
                    </button>
                )}
            </div>
            
            {/* Progress bar */}
            <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#E5E7EB',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px'
            }}>
                <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    backgroundColor: progressColor,
                    transition: 'all 0.3s ease',
                    borderRadius: '4px'
                }} />
            </div>
            
            {/* Message based on usage */}
            {isAtLimit ? (
                <div style={{
                    fontSize: '12px',
                    color: '#DC2626',
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <span>⚠️</span>
                    You've reached your {propertiesLabelLower} limit. 
                    {maxProperties <= 3 && ` Upgrade to Passport to add more ${propertiesLabelLower}.`}
                    {maxProperties > 3 && maxProperties <= 10 && ` Upgrade to Visa to add more ${propertiesLabelLower}.`}
                </div>
            ) : remainingProperties <= 2 ? (
                <div style={{
                    fontSize: '12px',
                    color: textColor,
                    marginTop: '8px'
                }}>
                    {remainingProperties === 1 ? 
                        `You can add 1 more ${propertiesLabelLower.slice(0, -1)} on this plan.` :
                        `You can add ${remainingProperties} more ${propertiesLabelLower} on this plan.`
                    }
                </div>
            ) : (
                <div style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    marginTop: '8px'
                }}>
                    {remainingProperties} {propertiesLabelLower} remaining
                </div>
            )}
        </div>
    );
};

const HostProfileTab = () => {
    const [hostProfile, setHostProfile] = useState(null);
    const [introVideo, setIntroVideo] = useState(null);
    const [showcaseAssets, setShowcaseAssets] = useState([]);
    const [introVideoSaveStatus, setIntroVideoSaveStatus] = useState('');
    const email = typeof window !== 'undefined' ? window.localStorage.getItem('postaway_email') || '' : '';
    const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';

    useEffect(() => {
        if (!email) return;
        let isMounted = true;
        (async () => {
            try {
                const response = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
                const data = await response.json();
                if (!isMounted) return;
                if (data.ok && data.profile) {
                    setHostProfile(data.profile);
                    setIntroVideo(data.profile.intro_video_url ? {
                        url: data.profile.intro_video_url,
                        hlsUrl: data.profile.intro_video_hls_url || '',
                        posterUrl: data.profile.intro_video_poster_url || '',
                        publicId: data.profile.intro_video_public_id || '',
                        duration: data.profile.intro_video_duration_sec || null,
                    } : null);
                }
            } catch (error) {
                console.error('Failed to fetch host profile:', error);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [apiBase, email]);

    useEffect(() => {
        const profileId = hostProfile?.host_profile_id;
        if (!profileId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${apiBase}/api/showcase?profile_kind=host&host_profile_id=${encodeURIComponent(profileId)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && data?.ok) {
                    setShowcaseAssets(Array.isArray(data.assets) ? data.assets : []);
                }
            } catch (err) {
                console.error('Failed to fetch showcase assets:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [apiBase, hostProfile?.host_profile_id]);

    const persistIntroVideo = async (nextVideo) => {
        try {
            const response = await fetch(`${apiBase}/api/host/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    brandName: hostProfile?.brand_name || null,
                    handle: hostProfile?.handle || null,
                    introVideo: nextVideo,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data.ok) {
                throw new Error(data.message || 'Failed to save intro video.');
            }
            return true;
        } catch (error) {
            console.error('Failed to save host intro video:', error);
            return false;
        }
    };

    const handleIntroVideoChange = async (nextVideo) => {
        setIntroVideo(nextVideo);
        setIntroVideoSaveStatus(nextVideo ? 'Saving intro video...' : 'Removing intro video...');
        const ok = await persistIntroVideo(nextVideo);
        setIntroVideoSaveStatus(
            ok
                ? (nextVideo ? 'Intro video saved.' : 'Intro video removed.')
                : 'Intro video could not be saved. Please try again.'
        );
        setTimeout(() => setIntroVideoSaveStatus(''), 3000);
    };

    return (
        <div className={styles.block}>
            <h4 className={cn("h4", styles.blockTitle)}>Your Host Profile</h4>
            <IntroVideoUpload value={introVideo} onChange={handleIntroVideoChange} />
            {introVideoSaveStatus && (
                <div style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: introVideoSaveStatus.includes('could not') ? '#dc1d08' : '#0f084b',
                    textAlign: 'center'
                }}>
                    {introVideoSaveStatus}
                </div>
            )}
            {hostProfile?.host_profile_id && (
                <div style={{ marginTop: 24 }}>
                    <ShowcaseUpload
                        mode="dashboard"
                        profileKind="host"
                        profileId={hostProfile.host_profile_id}
                        value={showcaseAssets}
                        onChange={setShowcaseAssets}
                    />
                </div>
            )}
        </div>
    );
};

// Property Overview Component
const PropertyOverview = ({ properties, onManageClick, hostCategory }) => {
    const activeProperties = properties.filter(p => p.status === 'active');
    const totalViews = properties.reduce((sum, p) => sum + (p.view_count || 0), 0);
    const totalInquiries = properties.reduce((sum, p) => sum + (p.inquiry_count || 0), 0);
    const propertiesLabel = getPropertyLabel(hostCategory, true);
    const propertiesLabelLower = getPropertyLabelLower(hostCategory, true);

    return (
        <div className={styles.propertyOverview}>
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{properties.length}</div>
                    <div className={styles.statLabel}>Total {propertiesLabel}</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{activeProperties.length}</div>
                    <div className={styles.statLabel}>Active Listings</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{totalViews}</div>
                    <div className={styles.statLabel}>Total Views</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{totalInquiries}</div>
                    <div className={styles.statLabel}>Total Inquiries</div>
                </div>
            </div>
            
            {properties.length > 0 ? (
                <div className={styles.propertyList}>
                    {properties.slice(0, 3).map(property => (
                        <div key={property.id} className={styles.propertyCard}>
                            <div
                                className={styles.propertyImage}
                                onClick={onManageClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onManageClick && onManageClick();
                                    }
                                }}
                                title={`Manage ${propertiesLabelLower}`}
                                style={{ cursor: onManageClick ? 'pointer' : 'default' }}
                            >
                                {property.main_image_url || property.image_url ? (
                                    <img src={property.main_image_url || property.image_url} alt={property.name} />
                                ) : (
                                    <div className={styles.propertyPlaceholder}>No image</div>
                                )}
                            </div>
                            <div className={styles.propertyInfo}>
                                <h5>{property.name}</h5>
                                <p>{(() => {
                                  const parts = [];
                                  if (property.location_city) parts.push(property.location_city);
                                  if (property.location_state) parts.push(property.location_state);
                                  if (property.location_country && property.location_country !== 'United States') {
                                    parts.push(property.location_country);
                                  }
                                  return parts.join(', ') || 'No location set';
                                })()}</p>
                                <div className={styles.propertyStats}>
                                    <span className={styles.propertyStat}>
                                        <Icon name="eye" size="14" /> {property.view_count || 0}
                                    </span>
                                    <span className={styles.propertyStat}>
                                        <Icon name="comment" size="14" /> {property.inquiry_count || 0}
                                    </span>
                                    <span className={cn(styles.propertyStatus, styles[`status-${property.status}`])}>
                                        {property.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <p>You haven't added any {propertiesLabelLower} yet.</p>
                </div>
            )}
        </div>
    );
};

const Overview = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hostProfile, setHostProfile] = useState(null);
    const [brandName, setBrandName] = useState('');
    const [isSavingBrandName, setIsSavingBrandName] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [introVideo, setIntroVideo] = useState(null);
    const [introVideoStatus, setIntroVideoStatus] = useState('');
    
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';

    useEffect(() => {
        fetchHostProfile();
        fetchProperties();
    }, []);

    const fetchHostProfile = async () => {
        if (!email) return;
        
        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
            const data = await response.json();
            
            if (data.ok && data.profile) {
                setHostProfile(data.profile);
                setBrandName(data.profile.brand_name || '');
                if (data.profile.intro_video_url || data.profile.intro_video_public_id) {
                    setIntroVideo({
                        url: data.profile.intro_video_url || null,
                        posterUrl: data.profile.intro_video_poster_url || null,
                        hlsUrl: data.profile.intro_video_hls_url || null,
                        publicId: data.profile.intro_video_public_id || null,
                        duration: data.profile.intro_video_duration_sec || null,
                    });
                } else {
                    setIntroVideo(null);
                }
            }
        } catch (error) {
            console.error('Failed to fetch host profile:', error);
        }
    };

    const handleIntroVideoChange = async (nextVideo) => {
        setIntroVideo(nextVideo);
        setIntroVideoStatus(nextVideo ? 'Saving intro video...' : 'Removing intro video...');
        try {
            const apiBase = window.location.hostname === 'localhost' ?
                'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/host/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    brandName: hostProfile?.brand_name || brandName?.trim() || null,
                    handle: hostProfile?.handle || null,
                    introVideo: nextVideo,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data.ok) {
                throw new Error(data.error || 'save_failed');
            }
            setIntroVideoStatus(nextVideo ? 'Intro video saved.' : 'Intro video removed.');
        } catch (err) {
            console.error('Failed to save host intro video:', err);
            setIntroVideoStatus('Could not save intro video. Please try again.');
        } finally {
            setTimeout(() => setIntroVideoStatus(''), 3000);
        }
    };

    const handleSaveBrandName = async () => {
        if (isSavingBrandName) return;
        
        setIsSavingBrandName(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            
            const response = await fetch(`${apiBase}/api/host/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    brandName: brandName?.trim() || null
                })
            });
            
            const data = await response.json();
            
            if (data.ok) {
                setHostProfile(prev => ({ ...prev, brand_name: brandName }));
                setShowSuccessModal(true);
                setTimeout(() => setShowSuccessModal(false), 3000);
            } else {
                alert('Failed to save brand name. Please try again.');
            }
        } catch (error) {
            console.error('Failed to save brand name:', error);
            alert('Failed to save brand name. Please try again.');
        } finally {
            setIsSavingBrandName(false);
        }
    };

    const fetchProperties = async () => {
        if (!email) {
            setLoading(false);
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            
            const response = await fetch(`${apiBase}/api/properties/list?email=${encodeURIComponent(email)}`);
            const data = await response.json();
            
            if (data.ok) {
                setProperties(data.properties || []);
            }
        } catch (error) {
            console.error('Failed to fetch properties:', error);
        } finally {
            setLoading(false);
        }
    };

    const propertyLabel = getPropertyLabel(hostProfile?.host_category);
    const propertiesLabel = getPropertyLabel(hostProfile?.host_category, true);

    return (
        <div className={styles.overview}>
            <div className={styles.block}>
                <div className={styles.blockHeader}>
                    <h4 className={cn("h4", styles.blockTitle)}>Your {propertiesLabel}</h4>
                    <div className={styles.actions}>
                        <button 
                            className={cn("button-stroke button-small", styles.action)}
                            onClick={() => navigate(`/property/editor?email=${encodeURIComponent(email)}`)}
                        >
                            Add {propertyLabel}
                        </button>
                        <button 
                            className={cn("button-stroke button-small", styles.action)}
                            onClick={() => navigate(`/property-management?email=${encodeURIComponent(email)}`)}
                        >
                            Manage {propertiesLabel}
                        </button>
                    </div>
                </div>
                {!loading && (
                    <PropertyOverview 
                        properties={properties}
                        onManageClick={() => navigate(`/property-management?email=${encodeURIComponent(email)}`)}
                        hostCategory={hostProfile?.host_category}
                    />
                )}
            </div>

            <div className={styles.block}>
                <div className={styles.blockHeader}>
                    <h4 className={cn("h4", styles.blockTitle)}>
                        Publicly Displayed Host Name
                        <Tippy content="Most often an actual name, brand name, or entity name.">
                            <span style={{ 
                                marginLeft: '8px', 
                                cursor: 'help',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#777E90'
                            }}>
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="18" 
                                    height="18" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M12 16v-4"/>
                                    <path d="M12 8h.01"/>
                                </svg>
                            </span>
                        </Tippy>
                    </h4>
                </div>
                <div style={{ padding: '16px 0' }}>
                    <input
                        type="text"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        placeholder="Enter your brand or host name"
                        style={{
                            width: '100%',
                            maxWidth: '500px',
                            padding: '12px 16px',
                            fontSize: '16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#0f084b'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                    <button
                        onClick={handleSaveBrandName}
                        disabled={isSavingBrandName}
                        className={cn("button-small", styles.saveBrandNameBtn)}
                        style={{
                            marginLeft: '12px',
                            backgroundColor: '#0f084b',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: isSavingBrandName ? 'not-allowed' : 'pointer',
                            opacity: isSavingBrandName ? 0.6 : 1,
                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isSavingBrandName) {
                                e.currentTarget.style.backgroundColor = '#1a0f6b';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#0f084b';
                        }}
                    >
                        {isSavingBrandName ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {showSuccessModal && (
                <>
                    <div 
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 999
                        }}
                        onClick={() => setShowSuccessModal(false)}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        padding: '32px',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '300px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '16px',
                            color: '#10B981'
                        }}>
                            ✓
                        </div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#0f084b',
                            marginBottom: '8px',
                            fontFamily: "'Sailors', serif"
                        }}>
                            Brand Name Saved!
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6B7280',
                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
                        }}>
                            Your publicly displayed host name has been updated successfully.
                        </p>
                    </div>
                </>
            )}

            <div className={styles.block}>
                <div className={styles.blockHeader}>
                    <h4 className={cn("h4", styles.blockTitle)}>
                        Your Intro Video
                        <Tippy content="A short 30-second video that plays muted on hover anywhere your profile picture is shown.">
                            <span style={{
                                marginLeft: '8px',
                                cursor: 'help',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#777E90'
                            }}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M12 16v-4"/>
                                    <path d="M12 8h.01"/>
                                </svg>
                            </span>
                        </Tippy>
                    </h4>
                </div>
                <div style={{ padding: '16px 0', maxWidth: 520 }}>
                    <IntroVideoUpload
                        value={introVideo}
                        onChange={handleIntroVideoChange}
                    />
                    {introVideoStatus && (
                        <div style={{
                            marginTop: 8,
                            fontSize: 13,
                            color: introVideoStatus.startsWith('Could') ? '#dc1d08' : '#0f084b',
                            textAlign: 'center'
                        }}>
                            {introVideoStatus}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.block}>
                <h4 className={cn("h4", styles.blockTitle)}>Recent reviews</h4>
                <Reviews />
            </div>
        </div>
    );
};

// Properties Tab Component
const PropertiesTab = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [hostProfile, setHostProfile] = useState(null);
    const [propertyLimits, setPropertyLimits] = useState({
        currentCount: 0,
        maxProperties: 1,
        minProperties: 0,
        planName: 'Free',
        remainingProperties: 1
    });
    
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';

    const fetchHostProfile = useCallback(async () => {
        if (!email) return;
        
        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
            const data = await response.json();
            
            if (data.ok && data.profile) {
                setHostProfile(data.profile);
            }
        } catch (error) {
            console.error('Failed to fetch host profile:', error);
        }
    }, [email]);

    const fetchProperties = useCallback(async () => {
        if (!email) return;

        setLoading(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            
            const response = await fetch(`${apiBase}/api/properties/list?email=${encodeURIComponent(email)}`);
            const data = await response.json();

            if (data.ok) {
                setProperties(data.properties || []);
                // Update property limits
                if (data.propertyLimits) {
                    setPropertyLimits(data.propertyLimits);
                }
            }
        } catch (error) {
            console.error('Failed to fetch properties:', error);
        } finally {
            setLoading(false);
        }
    }, [email]);

    useEffect(() => {
        fetchHostProfile();
        fetchProperties();
    }, [fetchHostProfile, fetchProperties]);

    const handleDelete = async (property) => {
        if (!window.confirm(`Are you sure you want to delete "${property.name}"?`)) {
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            
            const response = await fetch(`${apiBase}/api/properties/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, property_id: property.id })
            });

            const data = await response.json();
            if (data.ok) {
                setProperties(prev => prev.filter(p => p.id !== property.id));
            }
        } catch (error) {
            console.error('Failed to delete property:', error);
        }
    };

    const handleToggleStatus = async (property) => {
        const newStatus = property.status === 'active' ? 'inactive' : 'active';
        
        try {
            const apiBase = window.location.hostname === 'localhost' ? 
                'http://localhost:4000' : '';
            
            const response = await fetch(`${apiBase}/api/properties/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    email, 
                    property_id: property.id,
                    property: { status: newStatus }
                })
            });

            const data = await response.json();
            if (data.ok) {
                setProperties(prev => prev.map(p => 
                    p.id === property.id ? { ...p, status: newStatus } : p
                ));
            }
        } catch (error) {
            console.error('Failed to update property status:', error);
        }
    };

    const filteredProperties = properties.filter(property => {
        if (filter === 'all') return true;
        if (filter === 'featured') return property.featured;
        return property.status === filter;
    });

    const isAtLimit = propertyLimits.currentCount >= propertyLimits.maxProperties;
    const propertyLabel = getPropertyLabel(hostProfile?.host_category);
    const propertiesLabel = getPropertyLabel(hostProfile?.host_category, true);
    const propertiesLabelLower = getPropertyLabelLower(hostProfile?.host_category, true);
    
    const handleUpgrade = () => {
        // Navigate to host checkout with appropriate plan preselected
        let plan = '';
        if (propertyLimits.maxProperties <= 3) {
            plan = 'passport';
        } else if (propertyLimits.maxProperties <= 10) {
            plan = 'visa';
        }
        navigate(`/host-checkout${plan ? `?plan=${plan}` : ''}`);
    };

    return (
        <div className={styles.propertiesTab}>
            <div className={styles.propertiesHeader}>
                <h2 className={cn("h2", styles.propertiesTitle)}>Your {propertiesLabel}</h2>
                <button 
                    className={cn("button", styles.addPropertyBtn, { [styles.disabled]: isAtLimit })}
                    onClick={() => {
                        if (!isAtLimit) {
                            navigate(`/property/editor?email=${encodeURIComponent(email)}`);
                        }
                    }}
                    disabled={isAtLimit}
                    title={isAtLimit ? `You've reached your ${propertiesLabelLower} limit of ${propertyLimits.maxProperties} ${propertiesLabelLower}` : ''}
                    style={{
                        opacity: isAtLimit ? 0.5 : 1,
                        cursor: isAtLimit ? 'not-allowed' : 'pointer'
                    }}
                >
                    + Add {propertyLabel}
                </button>
            </div>
            
            {/* Property Limit Progress Bar - Only show in Properties tab */}
            <PropertyLimitProgress 
                currentCount={propertyLimits.currentCount}
                maxProperties={propertyLimits.maxProperties}
                planName={propertyLimits.planName}
                onUpgrade={handleUpgrade}
                hostCategory={hostProfile?.host_category}
            />

            {properties.length > 0 && (
                <div className={styles.propertiesFilters}>
                    <button 
                        className={cn(styles.filterBtn, { [styles.active]: filter === 'all' })}
                        onClick={() => setFilter('all')}
                    >
                        All ({properties.length})
                    </button>
                    <button 
                        className={cn(styles.filterBtn, { [styles.active]: filter === 'active' })}
                        onClick={() => setFilter('active')}
                    >
                        Active ({properties.filter(p => p.status === 'active').length})
                    </button>
                    <button 
                        className={cn(styles.filterBtn, { [styles.active]: filter === 'draft' })}
                        onClick={() => setFilter('draft')}
                    >
                        Draft ({properties.filter(p => p.status === 'draft' || !p.status).length})
                    </button>
                    <button 
                        className={cn(styles.filterBtn, { [styles.active]: filter === 'inactive' })}
                        onClick={() => setFilter('inactive')}
                    >
                        Inactive ({properties.filter(p => p.status === 'inactive').length})
                    </button>
                    <button 
                        className={cn(styles.filterBtn, { [styles.active]: filter === 'featured' })}
                        onClick={() => setFilter('featured')}
                    >
                        Featured ({properties.filter(p => p.featured).length})
                    </button>
                </div>
            )}

            {loading ? (
                <div className={styles.loading}>Loading {propertiesLabelLower}...</div>
            ) : filteredProperties.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3>No {propertiesLabelLower} found</h3>
                    <p>{properties.length === 0 ? `Add your first ${propertiesLabelLower.slice(0, -1)} to get started` : `No ${propertiesLabelLower} match the selected filter`}</p>
                    {properties.length === 0 && (
                        <button 
                            className={cn("button", styles.emptyStateBtn)}
                            onClick={() => navigate(`/property/editor?email=${encodeURIComponent(email)}`)}
                        >
                            Add Your First {propertyLabel}
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.propertiesGrid}>
                    {filteredProperties.map(property => (
                        <div key={property.id} className={styles.propertyItem}>
                            {property.featured && (
                                <div className={styles.featuredBadge}>Featured</div>
                            )}
                            <div
                                className={styles.propertyImage}
                                onClick={() => navigate(`/property-management?email=${encodeURIComponent(email)}`)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate(`/property-management?email=${encodeURIComponent(email)}`);
                                    }
                                }}
                                title={`Manage ${propertiesLabelLower}`}
                                style={{ cursor: 'pointer' }}
                            >
                                {property.main_image_url || property.image_url ? (
                                    <img src={property.main_image_url || property.image_url} alt={property.name} />
                                ) : (
                                    <div className={styles.imagePlaceholder}>No image</div>
                                )}
                            </div>
                            <div className={styles.propertyContent}>
                                <h4 className={styles.propertyName}>{property.name || `Unnamed ${propertyLabel}`}</h4>
                                <p className={styles.propertyLocation}>
                                    {(() => {
                                      const parts = [];
                                      if (property.location_city) parts.push(property.location_city);
                                      if (property.location_state) parts.push(property.location_state);
                                      if (property.location_country && property.location_country !== 'United States') {
                                        parts.push(property.location_country);
                                      }
                                      return parts.length ? parts.join(', ') : 'No location set';
                                    })()}
                                </p>
                                <div className={styles.propertyMeta}>
                                    {property.bedrooms && (
                                        <span>{property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}</span>
                                    )}
                                    {property.bathrooms != null && (
                                        <span>{Number(property.bathrooms)} bath{Number(property.bathrooms) !== 1 ? 's' : ''}</span>
                                    )}
                                    <span className={cn(styles.statusBadge, styles[`status-${property.status || 'draft'}`])}>
                                        {property.status || 'draft'}
                                    </span>
                                </div>
                                <div className={styles.propertyStats}>
                                    <div className={styles.stat}>
                                        <span className={styles.statValue}>{property.view_count || 0}</span>
                                        <span className={styles.statLabel}>Views</span>
                                    </div>
                                    <div className={styles.stat}>
                                        <span className={styles.statValue}>{property.inquiry_count || 0}</span>
                                        <span className={styles.statLabel}>Inquiries</span>
                                    </div>
                                </div>
                                <div className={styles.propertyActions}>
                                    <button 
                                        className={cn(styles.actionBtn, styles.editBtn)}
                                        onClick={() => navigate(`/property/editor?email=${encodeURIComponent(email)}&id=${property.id}`)}
                                    >
                                        Edit
                                    </button>
                                    {property.status === 'active' ? (
                                        <Tippy content="By making a property inactive it just means you are not accepting collaborations at this time.">
                                            <button 
                                                className={cn(styles.actionBtn, styles.statusBtn)}
                                                onClick={() => handleToggleStatus(property)}
                                            >
                                                Deactivate
                                            </button>
                                        </Tippy>
                                    ) : (
                                        <button 
                                            className={cn(styles.actionBtn, styles.statusBtn)}
                                            onClick={() => handleToggleStatus(property)}
                                        >
                                            Activate
                                        </button>
                                    )}
                                    <button 
                                        className={cn(styles.actionBtn, styles.deleteBtn)}
                                        onClick={() => handleDelete(property)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const HostDashboard = () => {
    const navigate = useNavigate();
    const { activePersona, switchPersona } = usePersona();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const { initializeChat, isConnected } = useChatContext();
    
    const [activeTab, setActiveTab] = useState(() => {
        if (tabParam) {
            const tabMap = {
                'profile': 'Profile',
                'properties': 'Properties',
                'messages': 'Messages',
                'reviews': 'Reviews',
                'settings': 'Settings',
                'bookings': 'Bookings',
                'payouts': 'Payouts',
                'calendar': 'Calendar'
            };
            return tabMap[tabParam.toLowerCase()] || 'Overview';
        }
        return 'Overview';
    });
    
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';
    const authToken = typeof window !== 'undefined' ?
        window.localStorage.getItem('postaway_auth_token') : null;
    
    // Referral link state for copy functionality
    const [referralUrl, setReferralUrl] = useState(null);
    const [copyStatus, setCopyStatus] = useState('idle'); // 'idle' | 'copying' | 'copied' | 'failed'
    const [showReferralUrl, setShowReferralUrl] = useState(false); // Show URL text field as fallback
    
    const apiBase = useMemo(() => (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '', []);

    // Pre-fetch referral URL on mount so it's ready for immediate use
    useEffect(() => {
        const fetchReferralUrl = async () => {
            const userEmail = localStorage.getItem('postaway_email') || '';
            if (!userEmail) return;
            try {
                const resp = await fetch(`${apiBase}/api/rewardful/referral-link?email=${encodeURIComponent(userEmail)}`);
                const data = await resp.json();
                if (data.ok && data.url) {
                    setReferralUrl(data.url);
                }
            } catch (err) {
                console.error('Failed to pre-fetch referral URL:', err);
            }
        };
        fetchReferralUrl();
    }, [apiBase]);

    // Helper to copy text to clipboard with mobile-friendly fallback
    const copyToClipboard = useCallback(async (text) => {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch (err) {
                console.warn('Clipboard API failed:', err);
            }
        }
        // Fallback for mobile and older browsers
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            textarea.style.top = '0';
            document.body.appendChild(textarea);
            
            // iOS specific handling
            const range = document.createRange();
            range.selectNodeContents(textarea);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textarea.setSelectionRange(0, 999999);
            
            const success = document.execCommand('copy');
            document.body.removeChild(textarea);
            return success;
        } catch (err) {
            console.warn('Fallback clipboard copy failed:', err);
            return false;
        }
    }, []);

    // Helper to handle copy referral link button
    const handleCopyReferralLink = useCallback(async () => {
        if (!referralUrl) {
            // URL not loaded yet - show it so user can manually copy
            setShowReferralUrl(true);
            window.open('https://postaway.getrewardful.com/signup', '_blank', 'noopener');
            return;
        }
        
        setCopyStatus('copying');
        const success = await copyToClipboard(referralUrl);
        
        if (success) {
            setCopyStatus('copied');
            setShowReferralUrl(false);
            setTimeout(() => setCopyStatus('idle'), 2000);
        } else {
            // Clipboard failed - show URL field so user can manually copy
            setCopyStatus('failed');
            setShowReferralUrl(true);
            setTimeout(() => setCopyStatus('idle'), 3000);
        }
    }, [referralUrl, copyToClipboard]);

    // Handle Invite a Friend click - uses cached URL
    const handleInviteFriend = useCallback(() => {
        if (referralUrl) {
            const subject = encodeURIComponent('Join me on Postaway');
            const body = encodeURIComponent(`Hey! Check out Postaway – my referral link: ${referralUrl}`);
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
        } else {
            // No referral URL - open signup
            window.open('https://postaway.getrewardful.com/signup', '_blank', 'noopener');
        }
    }, [referralUrl]);

    // Sync activePersona to 'host' as a side effect when the dashboard mounts
    // (and again only if the persona drifts away from 'host'). Kept in its own
    // effect so the subscription/chat init effect below doesn't re-fire every
    // time switchPersona's identity changes.
    useEffect(() => {
        if (!email) return;
        if (activePersona && activePersona !== 'host') {
            switchPersona('host').catch(() => {});
        }
    }, [email, activePersona, switchPersona]);

    useEffect(() => {
        if (!email) {
            navigate('/login');
            return;
        }

        // Check subscription status before allowing dashboard access
        (async () => {
            try {
                const res = await fetch('/api/access/subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                const data = await res.json();
                
                if (!res.ok || !data.ok || !data.active || !(data.roles || []).includes('host')) {
                    // No active subscription - check if user has completed profile
                    console.warn('Host dashboard: No active subscription');
                    try {
                        const profileRes = await fetch(`/api/host/profile?email=${encodeURIComponent(email)}`);
                        const profileData = await profileRes.json();
                        
                        if (profileData.ok && profileData.profile && profileData.profile.host_profile_id) {
                            // User has profile - skip to checkout page
                            navigate(`/host/checkout?email=${encodeURIComponent(email)}`);
                        } else {
                            // No profile - send to onboarding first
                            navigate(`/host/onboarding?email=${encodeURIComponent(email)}`);
                        }
                    } catch (profileError) {
                        console.error('Profile check error:', profileError);
                        // On error, default to onboarding
                        navigate(`/host/onboarding?email=${encodeURIComponent(email)}`);
                    }
                    return;
                }
                
                // User has active subscription - initialize chat if needed
                if (authToken && !isConnected) {
                    initializeChat(authToken).catch(console.error);
                }
            } catch (error) {
                console.error('Subscription check error:', error);
                // On error, redirect to onboarding to be safe
                navigate(`/host/onboarding?email=${encodeURIComponent(email)}`);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, authToken, isConnected, initializeChat, navigate]);

    // Update tab based on URL parameter changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) {
            const tabMap = {
                'properties': 'Properties',
                'messages': 'Messages',
                'reviews': 'Reviews',
                'settings': 'Settings',
                'bookings': 'Bookings',
                'payouts': 'Payouts',
                'calendar': 'Calendar'
            };
            const mappedTab = tabMap[tab.toLowerCase()];
            if (mappedTab) {
                setActiveTab(mappedTab);
            }
        } else {
            setActiveTab('Overview');
        }
    }, [location.search]);

    const dashboardContent = (
        <div className={styles.dashboardFullWidth}>
            <div className={styles.dashboardWrapper}>
                <div className={styles.inviteSection}>
                    <Tippy content="We manage our affiliate network through Rewardful. Click here to sign up or view your payout balance.">
                        <span style={{ display: 'inline-flex', alignItems: 'center', color: '#6B7280' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4"/>
                                <path d="M12 8h.01"/>
                            </svg>
                        </span>
                    </Tippy>
                    <button
                        type="button"
                        className={cn('button-stroke', styles.action)}
                        style={{ textDecoration: 'none', borderColor: '#0f084b', cursor: 'pointer' }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#0f084b';
                            e.currentTarget.style.color = '#ffffff';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onClick={handleInviteFriend}
                    >
                        Invite a Friend
                    </button>
                    <button
                        type="button"
                        className={cn('button-stroke', styles.action)}
                        style={{ 
                            textDecoration: 'none', 
                            borderColor: '#0f084b', 
                            cursor: copyStatus === 'copying' ? 'wait' : 'pointer',
                            minWidth: 140
                        }}
                        onMouseEnter={e => {
                            if (copyStatus !== 'copying') {
                                e.currentTarget.style.backgroundColor = '#0f084b';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.style.borderColor = '#0f084b';
                            }
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onClick={handleCopyReferralLink}
                        disabled={copyStatus === 'copying'}
                    >
                        {copyStatus === 'copying' ? 'Copying...' : 
                         copyStatus === 'copied' ? 'Copied!' : 
                         copyStatus === 'failed' ? 'Tap to copy manually' : 
                         'Copy Referral Link'}
                    </button>
                </div>
                {/* Fallback: show referral URL when clipboard copy fails on mobile */}
                {showReferralUrl && referralUrl && (
                    <div style={{ 
                        marginBottom: 16, 
                        padding: '12px 16px', 
                        background: '#f0f4f8', 
                        borderRadius: 8,
                        border: '1px solid #d1d5db'
                    }}>
                        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                            Your referral link (tap and hold to copy):
                        </div>
                        <input
                            type="text"
                            readOnly
                            value={referralUrl}
                            onClick={(e) => e.target.select()}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: 14,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                background: 'white',
                                color: '#0f084b',
                                fontFamily: 'monospace'
                            }}
                        />
                    </div>
                )}
                {activeTab === "Overview" && <Overview />}
                {activeTab === "Profile" && <HostProfileTab />}
                {activeTab === "Properties" && <PropertiesTab />}
                {activeTab === "Bookings" && (
                    <div className={styles.placeholder}>Bookings coming soon</div>
                )}
                {activeTab === "Messages" && (
                    <div className={styles.messagesContainer}>
                        <iframe 
                            src="/messages" 
                            style={{
                                width: '100%',
                                height: '700px',
                                border: 'none',
                                borderRadius: '8px'
                            }}
                            title="Messages"
                        />
                    </div>
                )}
                {activeTab === "Reviews" && (
                    <div>
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Pending Reviews</h3>
                            <PendingReviews />
                        </div>
                        <div>
                            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Your Reviews</h3>
                            <Reviews />
                        </div>
                    </div>
                )}
                {activeTab === "Payouts" && (
                    <div className={styles.placeholder}>Payouts coming soon</div>
                )}
                {activeTab === "Calendar" && (
                    <div className={styles.placeholder}>Calendar coming soon</div>
                )}
                {activeTab === "Settings" && <Settings userType="host" />}
            </div>
        </div>
    );

    return (
        <InfoPage 
            title={`Host Dashboard${activeTab !== 'Overview' ? ' - ' + activeTab : ''}`}
            below={dashboardContent}
            tallOverride={false}
            customMinHeight={800}
            disableStamps
        />
    );
};

export default HostDashboard;


